/**
 * iter_211 (2026-02-28) — Customer Ordering Smart Calendar & Slot Picker.
 *
 * Replaces the native `datetime-local` control on Customer Order with
 * a backend-driven booking picker. The backend is the ONLY source of
 * truth. This component never calculates available times locally — it
 * consumes three public endpoints:
 *
 *   GET /api/public/ordering/availability/{venue_id}?order_type=…
 *   GET /api/public/ordering/month/{venue_id}?order_type=…&month=YYYY-MM
 *   GET /api/public/ordering/slots/{venue_id}?order_type=…&date=YYYY-MM-DD
 *
 * Props:
 *   venueId     — string
 *   orderType   — "takeaway" | "pickup" | "delivery" | "dine_in"
 *   source      — "online" | "qr" | "kiosk"
 *   value       — currently selected slot, as a `datetime-local` local
 *                 string ("YYYY-MM-DDTHH:mm") so the parent's existing
 *                 `localToISO()` conversion path keeps working.
 *   onChange(localStr)   — called with the picked slot in local string
 *                          format. Pass "" to clear.
 *   onAvailabilityChange(open: bool)  — fired whenever the open/closed
 *                                       state for the current order_type
 *                                       changes; lets the parent auto-
 *                                       switch ASAP → Preorder.
 *
 * Strict scope: no payment / kitchen / printing / KDS interaction.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { minLocalScheduleString, localToISO, fmtScheduledChip } from "@/lib/schedule";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


// ── Date helpers (UI only — never used for validity decisions) ────
function pad2(n) { return String(n).padStart(2, "0"); }
function ymd(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function ymdMonth(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
}
function dayLabel(dateStr) {
  // Compare against the user's clock so "Today" / "Tomorrow" match the
  // device. The actual open/closed decision still comes from backend.
  const now = new Date();
  const today = ymd(now);
  const tom = new Date(now.getTime() + 86400000);
  if (dateStr === today) return "Today";
  if (dateStr === ymd(tom)) return "Tomorrow";
  const [, , d] = dateStr.split("-");
  const dt = new Date(`${dateStr}T00:00:00`);
  const wd = dt.toLocaleDateString(undefined, { weekday: "short" });
  return `${wd} ${parseInt(d, 10)}`;
}
function isoToLocalDtString(iso) {
  // Convert a UTC ISO instant to the "YYYY-MM-DDTHH:mm" string that
  // the rest of CustomerOrder expects (matches the old datetime-local
  // value). All math is local-device-clock based; the customer chose
  // the slot already so the rendering is purely cosmetic.
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  } catch { return ""; }
}
function fmtSlotTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}


// ── Component ────────────────────────────────────────────────────
export default function ScheduleOrderPicker({
  venueId, orderType, source = "online",
  value, onChange, onAvailabilityChange,
}) {
  const [availability, setAvailability] = useState(null);
  const [monthDate, setMonthDate] = useState(() => new Date());
  const [monthDays, setMonthDays] = useState({}); // {YYYY-MM-DD: bool}
  const [monthLoading, setMonthLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotError, setSlotError] = useState("");

  // Re-fetch availability whenever the order_type or venue changes.
  useEffect(() => {
    if (!venueId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(
          `${API}/public/ordering/availability/${venueId}`,
          { params: { order_type: orderType, source } });
        if (cancelled) return;
        const body = r?.data || r;
        setAvailability(body);
        onAvailabilityChange?.(!!body?.open);
        // If the venue isn't configured for the new ordering windows,
        // we surface that gracefully — the parent still allows ASAP
        // via the older delivery_schedule path.
      } catch {
        if (cancelled) return;
        setAvailability({ open: true, configured: false });
        onAvailabilityChange?.(true);
      }
    })();
    return () => { cancelled = true; };
  }, [venueId, orderType, source, onAvailabilityChange]);

  // Re-fetch the month overview whenever the visible month / venue /
  // order_type changes.
  useEffect(() => {
    if (!venueId) return;
    let cancelled = false;
    setMonthLoading(true);
    (async () => {
      try {
        const r = await axios.get(
          `${API}/public/ordering/month/${venueId}`,
          { params: { order_type: orderType, source,
                       month: ymdMonth(monthDate) } });
        if (cancelled) return;
        setMonthDays((r?.data || r)?.days || {});
      } catch {
        if (cancelled) return;
        setMonthDays({});
      } finally {
        if (!cancelled) setMonthLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [venueId, orderType, source, monthDate]);

  // When availability comes back with a `next_available_at`, default
  // the selected date to that day if the user hasn't picked one yet.
  useEffect(() => {
    if (selectedDate) return;
    const next = availability?.next_available_at;
    if (next) {
      const d = new Date(next);
      if (!Number.isNaN(d.getTime())) setSelectedDate(ymd(d));
    } else if (availability?.open && !selectedDate) {
      setSelectedDate(ymd(new Date()));
    }
  }, [availability, selectedDate]);

  // Re-fetch slots whenever the date or order_type changes.
  useEffect(() => {
    if (!venueId || !selectedDate) {
      setSlots([]); return;
    }
    let cancelled = false;
    setSlotsLoading(true); setSlotError("");
    (async () => {
      try {
        const r = await axios.get(
          `${API}/public/ordering/slots/${venueId}`,
          { params: { order_type: orderType, source, date: selectedDate } });
        if (cancelled) return;
        setSlots((r?.data || r)?.slots || []);
      } catch (e) {
        if (cancelled) return;
        setSlots([]);
        setSlotError("Could not load times — please try again.");
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [venueId, orderType, source, selectedDate]);

  // Group slots into sessions when there's a gap larger than 60 min
  // between consecutive slots (covers split shifts).
  const sessions = useMemo(() => groupSlotsBySession(slots), [slots]);

  // Selected slot tracked via the `value` prop (the parent owns it).
  const selectedISO = useMemo(() => {
    if (!value) return "";
    // value is "YYYY-MM-DDTHH:mm" local. We can't reverse it to UTC
    // exactly without tz info, but we can compare against slot list
    // by formatting each slot back to local-string form.
    return value;
  }, [value]);

  const grid = useMemo(() => buildMonthGrid(monthDate), [monthDate]);

  // iter_RESTORE_HOTFIX (2026-02-28, P0) — Legacy fallback.
  //
  // When the venue has NOT configured the new `venue_hours` structure,
  // `availability.configured === false` and `venue_slots_for_date()`
  // returns [] for every date — which would render the entire calendar
  // disabled and surface a misleading "Online ordering is currently
  // unavailable" empty state, blocking Preorder for venues that worked
  // perfectly before iter_211. Render the pre-iter_211 native
  // datetime-local picker in that case so customers can still
  // preorder. The backend's own `validate_scheduled_for_against_
  // venue_hours` is also a no-op for unconfigured venues, so this
  // matches backend semantics.
  if (availability && availability.configured === false) {
    const minLocal = minLocalScheduleString();
    return (
      <div data-testid="co-schedule-picker" className="space-y-1">
        <input
          type="datetime-local"
          step={300}
          min={minLocal}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          data-testid="co-scheduled-input-legacy"
          className="w-full h-11 px-3 rounded-md border border-[rgba(28,25,23,0.12)] tabular"
        />
        <div className="text-[11px] text-[var(--ink-500)] tabular">
          Earliest: {fmtScheduledChip(localToISO(minLocal))} · 5-min steps
        </div>
      </div>
    );
  }

  const prevMonth = () => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() - 1);
    setMonthDate(d); setSelectedDate("");
  };
  const nextMonth = () => {
    const d = new Date(monthDate);
    d.setMonth(d.getMonth() + 1);
    setMonthDate(d); setSelectedDate("");
  };
  const monthLabel = monthDate.toLocaleDateString(undefined,
    { month: "long", year: "numeric" });

  const todayStr = ymd(new Date());
  // A day is bookable when:
  //   - it's part of the visible month
  //   - it's NOT in the past
  //   - the backend month overview marks it as open
  // (If the month overview is still loading, we leave the day visually
  // pending and disabled — never falsely clickable.)
  const isDateBookable = (dateStr) => {
    if (!dateStr) return false;
    if (dateStr < todayStr) return false;
    if (!Object.prototype.hasOwnProperty.call(monthDays, dateStr)) return false;
    return monthDays[dateStr] === true;
  };

  return (
    <div data-testid="co-schedule-picker" className="space-y-3">
      {availability && (
        <AvailabilityBanner availability={availability} />
      )}

      {/* Calendar */}
      <div className="bg-white border border-[rgba(28,25,23,0.12)] rounded-md p-3">
        <div className="flex items-center justify-between mb-2">
          <button
            type="button" data-testid="co-cal-prev"
            onClick={prevMonth}
            className="w-8 h-8 rounded-md border border-[rgba(28,25,23,0.12)] active:scale-95"
            aria-label="Previous month"
          >‹</button>
          <div className="text-sm font-medium" data-testid="co-cal-label">
            {monthLabel} {monthLoading && <span className="text-stone-400 text-xs">…</span>}
          </div>
          <button
            type="button" data-testid="co-cal-next"
            onClick={nextMonth}
            className="w-8 h-8 rounded-md border border-[rgba(28,25,23,0.12)] active:scale-95"
            aria-label="Next month"
          >›</button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-stone-500 text-center mb-1">
          {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
            <div key={i}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1" data-testid="co-cal-grid">
          {grid.map((cell, i) => {
            if (cell === null) {
              return <div key={`x${i}`} className="h-10" />;
            }
            const dateStr = cell;
            const bookable = isDateBookable(dateStr);
            const isSelected = dateStr === selectedDate;
            const isToday = dateStr === todayStr;
            return (
              <button
                key={dateStr} type="button"
                data-testid={`co-cal-day-${dateStr}`}
                data-bookable={bookable ? "1" : "0"}
                disabled={!bookable}
                onClick={() => setSelectedDate(dateStr)}
                className={[
                  "h-10 rounded-md text-sm tabular border transition-all active:scale-95",
                  isSelected
                    ? "bg-[var(--ink-900)] text-[var(--sand-50)] border-[var(--ink-900)]"
                    : bookable
                      ? "bg-white border-[rgba(28,25,23,0.12)] hover:bg-stone-50"
                      : "bg-stone-100 border-stone-200 text-stone-400 cursor-not-allowed",
                  isToday && !isSelected ? "ring-1 ring-amber-400" : "",
                ].join(" ")}
              >
                {parseInt(dateStr.split("-")[2], 10)}
              </button>
            );
          })}
        </div>
        {selectedDate && (
          <div className="text-[12px] text-stone-500 mt-2">
            Showing slots for {dayLabel(selectedDate)}
          </div>
        )}
      </div>

      {/* Slots */}
      {selectedDate && (
        <div className="bg-white border border-[rgba(28,25,23,0.12)] rounded-md p-3"
              data-testid="co-slot-picker">
          {slotsLoading ? (
            <div className="text-sm text-stone-500 italic">Loading times…</div>
          ) : slotError ? (
            <div className="text-sm text-red-600">{slotError}</div>
          ) : slots.length === 0 ? (
            <EmptyState availability={availability} />
          ) : (
            <div className="space-y-3">
              {sessions.map((session, sidx) => (
                <div key={sidx} data-testid={`co-slot-session-${sidx}`}>
                  {sessions.length > 1 && (
                    <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">
                      {sessionLabel(session)}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {session.map((iso) => {
                      const lbl = fmtSlotTime(iso);
                      const localStr = isoToLocalDtString(iso);
                      const isSel = selectedISO === localStr;
                      return (
                        <button
                          key={iso} type="button"
                          data-testid={`co-slot-${localStr}`}
                          onClick={() => onChange?.(localStr)}
                          className={[
                            "min-w-[64px] h-10 px-3 rounded-md text-sm tabular active:scale-95 transition",
                            isSel
                              ? "bg-[var(--ink-900)] text-[var(--sand-50)]"
                              : "bg-white border border-[rgba(28,25,23,0.12)] hover:bg-stone-50",
                          ].join(" ")}
                        >{lbl}</button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {value && (
        <div className="text-[12px] text-stone-700 tabular pt-1"
              data-testid="co-schedule-readback">
          You picked: <span className="font-medium">{fmtSlotChip(value)}</span>
        </div>
      )}
    </div>
  );
}


// ── Sub-components ───────────────────────────────────────────────
function AvailabilityBanner({ availability }) {
  const open = !!availability?.open;
  const next = availability?.next_available_at;
  return (
    <div
      data-testid="co-availability-banner"
      className={[
        "rounded-md p-2 text-sm border",
        open
          ? "bg-emerald-50 border-emerald-200 text-emerald-900"
          : "bg-amber-50 border-amber-200 text-amber-900",
      ].join(" ")}
    >
      <div className="font-medium">
        {open ? "Open now" : "Closed for ASAP — preorder below"}
      </div>
      {!open && availability?.message && (
        <div className="text-xs mt-0.5">{availability.message}</div>
      )}
      {!open && next && (
        <div className="text-xs mt-0.5">
          Next available: {fmtNextAvailable(next)}
        </div>
      )}
    </div>
  );
}

function EmptyState({ availability }) {
  const next = availability?.next_available_at;
  return (
    <div data-testid="co-slot-empty"
          className="text-sm text-stone-700">
      <div className="font-medium">Online ordering is currently unavailable.</div>
      {next ? (
        <div className="text-xs mt-1">
          Next available: {fmtNextAvailable(next)}
        </div>
      ) : (
        <div className="text-xs mt-1 text-stone-500">
          Please check back later.
        </div>
      )}
    </div>
  );
}


// ── Internal helpers ─────────────────────────────────────────────
function buildMonthGrid(monthDate) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0).getDate();
  const startCol = first.getDay(); // 0=Sun
  const cells = [];
  for (let i = 0; i < startCol; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    cells.push(ymd(new Date(monthDate.getFullYear(), monthDate.getMonth(), d)));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function groupSlotsBySession(slots) {
  if (!Array.isArray(slots) || slots.length === 0) return [];
  const sorted = [...slots].sort();
  const out = [];
  let cur = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(sorted[i - 1]);
    const next = new Date(sorted[i]);
    const gapMin = (next - prev) / 60000;
    // A gap > 60 minutes between consecutive slots marks a new
    // session (split shift). Within a session slots are typically
    // 15–30 min apart.
    if (gapMin > 60) { out.push(cur); cur = [sorted[i]]; }
    else { cur.push(sorted[i]); }
  }
  if (cur.length) out.push(cur);
  return out;
}

function sessionLabel(session) {
  if (!session.length) return "";
  const first = new Date(session[0]);
  const h = first.getHours();
  if (h < 11) return "Morning";
  if (h < 15) return "Lunch";
  if (h < 17) return "Afternoon";
  return "Dinner";
}

function fmtSlotChip(localStr) {
  if (!localStr) return "";
  try {
    const d = new Date(localStr);
    return d.toLocaleDateString(undefined, {
      weekday: "short", day: "2-digit", month: "short",
    }) + " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return localStr; }
}

function fmtNextAvailable(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      weekday: "short", day: "2-digit", month: "short",
    }) + " at " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
